import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	List,
	ListItemButton,
	ListItemText,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { getTags } from "../../api/tags";

interface PersonTagProps {
	tags: string[];
	onTagsChange: (tags: string[]) => void;
	onRemoveTag: (tag: string) => void;
	showAddButton?: boolean;
}

export interface PersonTagRef {
	openDialog: () => void;
}

export const PersonTag = forwardRef<PersonTagRef, PersonTagProps>(
	({ tags, onTagsChange, onRemoveTag, showAddButton = true }, ref) => {
		const [options, setOptions] = useState<string[]>([]);
		const [dialogOpen, setDialogOpen] = useState(false);
		const [filter, setFilter] = useState("");
		const [selectedTag, setSelectedTag] = useState<string | null>(null);

		useEffect(() => {
			getTags()
				.then(setOptions)
				.catch((err) => console.log(err));
		}, []);

		const handleOpenDialog = () => {
			setFilter("");
			setSelectedTag(null);
			setDialogOpen(true);
		};

		useImperativeHandle(ref, () => ({
			openDialog: handleOpenDialog,
		}));

		const handleCloseDialog = () => {
			setDialogOpen(false);
			setFilter("");
			setSelectedTag(null);
		};

		const handleSave = () => {
			if (selectedTag && !tags.includes(selectedTag)) {
				onTagsChange([...tags, selectedTag]);
			}
			handleCloseDialog();
		};

		// Filter out already-selected tags and apply search filter
		const availableOptions = options
			.filter((opt) => !tags.includes(opt))
			.filter((opt) => opt.toLowerCase().includes(filter.toLowerCase()));

		const canCreateNew =
			filter.trim() &&
			!options.includes(filter.trim()) &&
			!tags.includes(filter.trim());

		return (
			<Box data-component="person-tag-container">
				{/* Display current tags as chips */}
				<Stack
					direction="row"
					spacing={1}
					flexWrap="wrap"
					useFlexGap
					data-component="tags-display"
				>
					{tags.length === 0 ? (
						<Typography variant="body2" color="text.secondary">
							No tags
						</Typography>
					) : (
						tags.map((tag) => (
							<Chip
								key={tag}
								label={tag}
								onDelete={() => onRemoveTag(tag)}
								data-component="tag-chip"
							/>
						))
					)}
				</Stack>

				{/* Add Tag button - only show if showAddButton is true */}
				{showAddButton && (
					<Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
						<Button
							variant="outlined"
							size="small"
							startIcon={<LocalOfferIcon />}
							onClick={handleOpenDialog}
							data-component="add-tag-button"
						>
							Add Tag
						</Button>
					</Stack>
				)}

				{/* Add Tag Dialog */}
				<Dialog
					open={dialogOpen}
					onClose={handleCloseDialog}
					maxWidth="xs"
					fullWidth
					data-component="add-tag-dialog"
				>
					<DialogTitle>Add Tag</DialogTitle>
					<DialogContent>
						<TextField
							fullWidth
							size="small"
							placeholder="Search or create new tag..."
							value={filter}
							onChange={(e) => {
								setFilter(e.target.value);
								// Auto-select "create new" when typing something new
								if (
									e.target.value.trim() &&
									!options.includes(e.target.value.trim()) &&
									!tags.includes(e.target.value.trim())
								) {
									setSelectedTag(e.target.value.trim());
								} else {
									setSelectedTag(null);
								}
							}}
							autoFocus
							sx={{ mb: 1 }}
							data-component="tag-search"
						/>
						<List
							dense
							sx={{ maxHeight: 300, overflow: "auto" }}
							data-component="tag-list"
						>
							{canCreateNew && (
								<ListItemButton
									selected={selectedTag === filter.trim()}
									onClick={() => setSelectedTag(filter.trim())}
									data-component="create-tag-item"
								>
									<ListItemText
										primary={`Create "${filter.trim()}"`}
										slotProps={{ primary: { color: "primary" } }}
									/>
								</ListItemButton>
							)}
							{availableOptions.length === 0 && !canCreateNew ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ p: 2, textAlign: "center" }}
								>
									{filter ? "No matching tags" : "No tags available"}
								</Typography>
							) : (
								availableOptions.map((tag) => (
									<ListItemButton
										key={tag}
										selected={selectedTag === tag}
										onClick={() => setSelectedTag(tag)}
										data-component="tag-item"
									>
										<ListItemText primary={tag} />
									</ListItemButton>
								))
							)}
						</List>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCloseDialog}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleSave}
							disabled={!selectedTag}
							data-component="save-tag-button"
						>
							Add
						</Button>
					</DialogActions>
				</Dialog>
			</Box>
		);
	}
);
